import {
  AuditTrailServiceApiAuditTrailServiceCloudEventsAccessSummaryRequest,
  AuditTrailServiceApiAuditTrailServiceCreateCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceDeleteCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceGetCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupsRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupBucketsRequest,
  AuditTrailServiceApiAuditTrailServiceUpdateCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsActivityGraphRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsResourceIdentitiesRequest,
  AuditTrailServiceApiAuditTrailServiceDownloadCloudEventsActivityAccessRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsBucketPathSuggestionsRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceEventsRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceHoursRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceSummaryRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourcesRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsSummaryRequest,
  V1CloudEventsAccessSummaryResponse,
  V1DownloadCloudEventsActivityAccessResponse,
  V1CreateCloudEventsActivityGroupResponse,
  V1GetCloudEventsActivityGroupResponse,
  V1ListCloudEventsActivityGroupsResponse,
  V1ListCloudEventsActivityGroupBucketsResponse,
  V1CloudEventsActivityGraphResponse,
  V1ListCloudEventsActivityAccessResponse,
  V1ListCloudEventsResourceIdentitiesResponse,
  V1CloudEventsBucketPathSuggestionsResponse,
  V1CloudEventsResourceEventsResponse,
  V1CloudEventsResourceHoursResponse,
  V1CloudEventsResourceSummaryResponse,
  V1CloudEventsResourcesResponse,
  V1CloudEventsSummaryResponse,
  V1UpdateCloudEventsActivityGroupResponse,
} from '@ariksa/audittrail/api';
import {
  CanAccessResourceSummaryCounts,
  InsightApiGetCanAccessResourceSummaryCountsRequest,
  LabelResponse,
  ResourceApiGetInventoryResourcesRequest,
  ResourceInfo,
  TagsApiGetLabelsRequest,
  TagsApiGetTagsResourceRequest,
  TagsRead,
} from '@ariksa/inventory-core/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import {
  AuditTrailApiService,
  authTokenHeader,
  InventoryService,
} from 'api/services';
import { doGetRiskContext } from 'containers/SharedState/saga';

import { actions } from './slice';

export function* incidentResponseSaga() {
  yield takeLatestAction(actions.getActivityGroups, doGetActivityGroups);
  yield takeLatestAction(actions.getActivityGroup, doGetActivityGroup);
  yield takeLatestAction(
    actions.getActivityGroupBuckets,
    doGetActivityGroupBuckets,
  );
  yield takeLatestAction(actions.createActivityGroup, doCreateActivityGroup);
  yield takeLatestAction(actions.updateActivityGroup, doUpdateActivityGroup);
  yield takeLatestAction(actions.deleteActivityGroup, doDeleteActivityGroup);
  yield takeLatestAction(actions.getLabels, doGetLabels);
  yield takeLatestAction(actions.getActivityGraph, doGetActivityGraph);
  yield takeLatestAction(
    actions.getActivityObjectPaths,
    doGetActivityObjectPaths,
  );
  yield takeLatestAction(
    actions.getActivityBucketContexts,
    doGetActivityBucketContexts,
  );
  yield takeLatestAction(actions.getActivityAccesses, doGetActivityAccesses);
  yield takeLatestAction(
    actions.getActivityIdentityCount,
    doGetActivityIdentityCount,
  );
  // Export activity graph accesses as CSV.
  yield takeLatestAction(
    actions.downloadActivityAccesses,
    doDownloadActivityAccesses,
  );
  yield takeLatestAction(actions.getActivityResources, doGetActivityResources);
  yield takeLatestAction(
    actions.getResourceIdentities,
    doGetResourceIdentities,
  );
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(actions.getResourceRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getCloudEventsAccessSummary,
    doGetCloudEventsAccessSummary,
  );
  yield takeLatestAction(
    actions.getCloudEventsSummary,
    doGetCloudEventsSummary,
  );
  yield takeLatestAction(
    actions.getCloudEventsResourceSummary,
    doGetCloudEventsResourceSummary,
  );
  yield takeLatestAction(
    actions.getCloudEventsResources,
    doGetCloudEventResources,
  );
  yield takeLatestAction(
    actions.getAggregateResourceEvents,
    doGetAggregatedResourceEvents,
  );
  yield takeLatestAction(actions.getResourceEvents, doGetResourceEvents);
  yield takeLatestAction(actions.getAccessSummary, doGetAccessSummary);
}

/*get activity groups*/
function* doGetActivityGroups(
  ctx: QueryContext<
    V1ListCloudEventsActivityGroupsResponse,
    AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceListCloudEventsActivityGroups(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events activity groups.',
    },
  );
}

/*get a single activity group*/
function* doGetActivityGroup(
  ctx: QueryContext<
    V1GetCloudEventsActivityGroupResponse,
    AuditTrailServiceApiAuditTrailServiceGetCloudEventsActivityGroupRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceGetCloudEventsActivityGroup(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events activity group.',
    },
  );
}

/*get buckets for an activity group*/
function* doGetActivityGroupBuckets(
  ctx: QueryContext<
    V1ListCloudEventsActivityGroupBucketsResponse,
    AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupBucketsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceListCloudEventsActivityGroupBuckets(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get activity group resources.',
    },
  );
}

/*create an activity group*/
function* doCreateActivityGroup(
  ctx: QueryContext<
    V1CreateCloudEventsActivityGroupResponse,
    AuditTrailServiceApiAuditTrailServiceCreateCloudEventsActivityGroupRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCreateCloudEventsActivityGroup(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to create cloud events activity group.',
      successMsg: 'Cloud events activity group created successfully.',
    },
  );
}

/*update an activity group*/
function* doUpdateActivityGroup(
  ctx: QueryContext<
    V1UpdateCloudEventsActivityGroupResponse,
    AuditTrailServiceApiAuditTrailServiceUpdateCloudEventsActivityGroupRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceUpdateCloudEventsActivityGroup(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to update cloud events activity group.',
      successMsg: 'Cloud events activity group updated successfully.',
    },
  );
}

/*delete an activity group*/
function* doDeleteActivityGroup(
  ctx: QueryContext<
    object,
    AuditTrailServiceApiAuditTrailServiceDeleteCloudEventsActivityGroupRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceDeleteCloudEventsActivityGroup(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to delete cloud events activity group.',
      successMsg: 'Cloud events activity group deleted successfully.',
    },
  );
}

/*get labels*/
function* doGetLabels(
  ctx: QueryContext<LabelResponse[], TagsApiGetLabelsRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Tags.getLabels(ctx.params), {
    errorMsg: 'Failed to get labels.',
  });
}

/*get cloud events activity graph*/
function* doGetActivityGraph(
  ctx: QueryContext<
    V1CloudEventsActivityGraphResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsActivityGraphRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsActivityGraph(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events activity graph.',
    },
  );
}

/*get object paths for the selected activity resources*/
function* doGetActivityObjectPaths(
  ctx: QueryContext<
    V1CloudEventsBucketPathSuggestionsResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsBucketPathSuggestionsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsBucketPathSuggestions(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events object paths.',
    },
  );
}

/*get contexts attached to the selected activity bucket*/
function* doGetActivityBucketContexts(
  ctx: QueryContext<TagsRead[], TagsApiGetTagsResourceRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Tags.getTagsResource(ctx.params),
    {
      errorMsg: 'Failed to get bucket contexts.',
    },
  );
}

/*get access summary*/
function* doGetAccessSummary(
  ctx: QueryContext<
    CanAccessResourceSummaryCounts[],
    InsightApiGetCanAccessResourceSummaryCountsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.Insight.getCanAccessResourceSummaryCounts(ctx.params),
    {
      errorMsg: 'Failed to get access summary counts.',
    },
  );
}

/*get activity graph table data*/
function* doGetActivityAccesses(
  ctx: QueryContext<
    V1ListCloudEventsActivityAccessResponse,
    AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceListCloudEventsActivityAccess(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events activity accesses.',
    },
  );
}

/*get identities observed for an activity resource*/
function* doGetResourceIdentities(
  ctx: QueryContext<
    V1ListCloudEventsResourceIdentitiesResponse,
    AuditTrailServiceApiAuditTrailServiceListCloudEventsResourceIdentitiesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceListCloudEventsResourceIdentities(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get resource identities.',
    },
  );
}

/*get total identities for the activity graph*/
function* doGetActivityIdentityCount(
  ctx: QueryContext<
    V1ListCloudEventsActivityAccessResponse,
    AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceListCloudEventsActivityAccess(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events activity identity count.',
    },
  );
}

/*download activity graph accesses as CSV.
  Added so the activity graph download uses the audittrail export API.
  Exported so its request can be unit tested.*/
export function* doDownloadActivityAccesses(
  ctx: QueryContext<
    V1DownloadCloudEventsActivityAccessResponse,
    AuditTrailServiceApiAuditTrailServiceDownloadCloudEventsActivityAccessRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceDownloadCloudEventsActivityAccess(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to download activity graph.',
    },
  );
}

/*get resources for the activity drawer filter*/
function* doGetActivityResources(
  ctx: QueryContext<ResourceInfo[], ResourceApiGetInventoryResourcesRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.Resource.getInventoryResources(ctx.params, {
        ...authTokenHeader(),
      }),
    {
      errorMsg: 'Failed to get activity resources.',
    },
  );
}

/*get cloud events access summary*/
function* doGetCloudEventsAccessSummary(
  ctx: QueryContext<
    V1CloudEventsAccessSummaryResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsAccessSummaryRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsAccessSummary(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events access summary.',
    },
  );
}

/*get cloud events resource summary*/
function* doGetCloudEventsResourceSummary(
  ctx: QueryContext<
    V1CloudEventsResourceSummaryResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsResourceSummaryRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsResourceSummary(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events resource summary.',
    },
  );
}

/*get cloud events summary*/
function* doGetCloudEventsSummary(
  ctx: QueryContext<
    V1CloudEventsSummaryResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsSummaryRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsSummary(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events summary.',
    },
  );
}

/*get cloud events resources*/
function* doGetCloudEventResources(
  ctx: QueryContext<
    V1CloudEventsResourcesResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsResourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsResources(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud event resources.',
    },
  );
}

export function* doGetAggregatedResourceEvents(
  ctx: QueryContext<
    V1CloudEventsResourceHoursResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsResourceHoursRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsResourceHours(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get aggregated cloud events for a resource!',
      // cacheKey: 'doGetAccessed',
    },
  );
}

/*get cloud events resources*/
function* doGetResourceEvents(
  ctx: QueryContext<
    V1CloudEventsResourceEventsResponse,
    AuditTrailServiceApiAuditTrailServiceCloudEventsResourceEventsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsResourceEvents(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get cloud events for a resource.',
    },
  );
}
